import React, { useState, useRef, useEffect } from 'react';
import {
  Add,
  Image as ImageIcon,
  TableChart,
  Code,
  HorizontalRule,
  VideoLibrary,
} from '@mui/icons-material';
import { IconButton, Tooltip, Paper, Divider } from '@mui/material';
import styles from './RichTextEditor.module.scss';

const HEADING_SHORTCUTS = [
  { label: 'H1', level: 1 },
  { label: 'H2', level: 2 },
  { label: 'H3', level: 3 },
];

export default function FloatingInsertMenu({ editor, onInsertImage, onInsertVideo }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  if (!editor) return null;

  const handleHeading = (level) => {
    editor.chain().focus().setHeading({ level }).run();
    setOpen(false);
  };

  const handleTable = () => {
    editor.chain().focus().insertTable({ rows: 2, cols: 2, withHeaderRow: true }).run();
    setOpen(false);
  };

  const handleCodeBlock = () => {
    editor.chain().focus().toggleCodeBlock().run();
    setOpen(false);
  };

  const handleDivider = () => {
    editor.chain().focus().setHorizontalRule().run();
    setOpen(false);
  };

  const handleImage = () => {
    setOpen(false);
    onInsertImage?.();
  };

  const handleVideo = () => {
    setOpen(false);
    onInsertVideo?.();
  };

  return (
    <div ref={containerRef} className={styles.floatingMenu}>
      <Tooltip title="Insert content" arrow placement="right">
        <IconButton
          size="small"
          onClick={() => setOpen((o) => !o)}
          className={`${styles.floatingAddBtn} ${open ? styles.floatingAddBtnOpen : ''}`}
        >
          <Add fontSize="small" />
        </IconButton>
      </Tooltip>

      {open && (
        <Paper elevation={3} className={styles.floatingPanel}>
          {/* Headings */}
          {HEADING_SHORTCUTS.map(({ label, level }) => (
            <Tooltip key={level} title={`Heading ${level}`} arrow placement="right">
              <IconButton
                size="small"
                onClick={() => handleHeading(level)}
                className={styles.floatingOption}
                sx={{ fontWeight: 700, fontSize: '0.7rem' }}
              >
                {label}
              </IconButton>
            </Tooltip>
          ))}

          <Divider sx={{ my: 0.5 }} />

          {/* Image */}
          <Tooltip title="Insert image" arrow placement="right">
            <IconButton size="small" onClick={handleImage} className={styles.floatingOption}>
              <ImageIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Table */}
          <Tooltip title="Insert table" arrow placement="right">
            <IconButton size="small" onClick={handleTable} className={styles.floatingOption}>
              <TableChart fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Code Block */}
          <Tooltip title="Code block" arrow placement="right">
            <IconButton size="small" onClick={handleCodeBlock} className={styles.floatingOption}>
              <Code fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Horizontal Rule */}
          <Tooltip title="Divider" arrow placement="right">
            <IconButton size="small" onClick={handleDivider} className={styles.floatingOption}>
              <HorizontalRule fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Video embed */}
          <Tooltip title="Embed video (YouTube / Instagram)" arrow placement="right">
            <IconButton size="small" onClick={handleVideo} className={styles.floatingOption}>
              <VideoLibrary fontSize="small" />
            </IconButton>
          </Tooltip>
        </Paper>
      )}
    </div>
  );
}
